/**
 * Minimal server-sent-events reader over fetch. The browser's EventSource cannot send a POST
 * body or an Authorization header, so the interview turn uses fetch + ReadableStream.
 */

export interface SseEvent {
  event: string;
  data: string;
}

/** Splits buffered text into complete events; returns the unconsumed remainder. */
export function parseSse(buffer: string): { events: SseEvent[]; rest: string } {
  const events: SseEvent[] = [];
  const normalized = buffer.replace(/\r\n/g, '\n');
  let start = 0;
  for (;;) {
    const end = normalized.indexOf('\n\n', start);
    if (end === -1) break;
    const block = normalized.slice(start, end);
    start = end + 2;
    let event = 'message';
    const data: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith(':')) continue;
      const i = line.indexOf(':');
      const field = i === -1 ? line : line.slice(0, i);
      const value = i === -1 ? '' : line.slice(i + 1).replace(/^ /, '');
      if (field === 'event') event = value;
      else if (field === 'data') data.push(value);
    }
    if (data.length) events.push({ event, data: data.join('\n') });
  }
  return { events, rest: normalized.slice(start) };
}

export async function* readSse(body: ReadableStream<Uint8Array>): AsyncGenerator<SseEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { events, rest } = parseSse(buffer);
    buffer = rest;
    for (const e of events) yield e;
  }
  const tail = parseSse(buffer + '\n\n');
  for (const e of tail.events) yield e;
}
